import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CalendarDays, Send, X, Check, Clock, Ban } from "lucide-react";

const CHECK_STATUS = {
  yes:     { color: "text-emerald-400", label: "Confirmed" },
  no:      { color: "text-red-400", label: "Declined" },
  pending: { color: "text-amber-400", label: "Pending" },
};

function formatDateRange(start, end) {
  if (!start) return "";
  const s = new Date(start + "T00:00:00");
  const fmt = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  if (!end || start === end) return fmt(s);
  return `${fmt(s)} - ${fmt(new Date(end + "T00:00:00"))}`;
}

export function CrewEventRow({ ev, canEdit, sending, onSendCheck, onOverride, onUnassign }) {
  const checkCfg = CHECK_STATUS[ev.check_status];
  const crewDatesStr = ev.crew_dates?.length
    ? ev.crew_dates.map(d => new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })).join(", ")
    : null;

  return (
    <div data-testid={`crew-event-row-${ev.event_id}`} className="flex items-start gap-1.5 text-xs group">
      <CalendarDays className="w-3 h-3 text-primary shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium truncate text-foreground">{ev.name}</span>
          <Badge variant="outline" className={`text-[9px] px-1 py-0 h-3.5 status-${ev.status}`}>{ev.status}</Badge>
        </div>
        <div className="text-muted-foreground/60 text-[10px]">
          {crewDatesStr || formatDateRange(ev.start_date, ev.end_date)}
          {ev.venue ? ` · ${ev.venue}` : ""}
        </div>
        {checkCfg && (
          <span className={`text-[10px] ${checkCfg.color}`}>
            {checkCfg.label === "Confirmed" && <Check className="w-2.5 h-2.5 inline mr-0.5" />}
            {checkCfg.label === "Pending" && <Clock className="w-2.5 h-2.5 inline mr-0.5" />}
            {checkCfg.label}
          </span>
        )}
      </div>

      {canEdit && (
        <div className="flex items-center gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {(!ev.check_status || ev.check_status === "pending") && (
            <Button
              data-testid={`crew-card-send-${ev.event_id}`}
              variant="ghost" size="icon" className="h-5 w-5 text-primary"
              onClick={() => onSendCheck(ev.event_id, ev.check_status === "pending")}
              disabled={sending}
              title={ev.check_status === "pending" ? "Send reminder" : "Send availability check"}
            >
              <Send className="w-2.5 h-2.5" />
            </Button>
          )}
          {ev.check_status !== "yes" && (
            <Button
              data-testid={`crew-card-confirm-${ev.event_id}`}
              variant="ghost" size="icon" className="h-5 w-5 text-emerald-400 hover:text-emerald-300"
              onClick={() => onOverride(ev.event_id, "confirmed")}
              title="Manually mark confirmed"
            >
              <Check className="w-2.5 h-2.5" />
            </Button>
          )}
          {ev.check_status !== "no" && (
            <Button
              data-testid={`crew-card-decline-${ev.event_id}`}
              variant="ghost" size="icon" className="h-5 w-5 text-amber-400 hover:text-amber-300"
              onClick={() => onOverride(ev.event_id, "declined")}
              title="Manually mark declined"
            >
              <Ban className="w-2.5 h-2.5" />
            </Button>
          )}
          <Button
            data-testid={`crew-card-unassign-${ev.event_id}`}
            variant="ghost" size="icon" className="h-5 w-5 text-red-400 hover:text-red-300"
            onClick={() => onUnassign(ev.event_id)}
            title="Unassign from event"
          >
            <X className="w-2.5 h-2.5" />
          </Button>
        </div>
      )}
    </div>
  );
}
